import {
  type MutableRefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingIntradayOrderStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import {
  BiddingIntradayOrderOrderMethod,
  type BiddingIntradayOrderWithdrawFailureReason,
  BiddingIntradayOrderWithdrawState,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  type BulkResultNotice,
  useBulkResultNotice,
} from '~/hooks/bidding/useBulkResultNotice'
import { invalidateIntradayOrderQueries } from '~/hooks/bidding/useWithdrawBiddingIntradayOrders'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { appToaster } from '~/libs/toaster'
import { isSameProtoDate } from '~/models/dateTime'
import { intradayBulkWithdrawNoticeSpec } from '~/pages/IntradayBidding/models/intradayBulkWithdrawNotice'
import { withdrawFailureTextKeys } from '~/pages/IntradayBidding/models/withdrawFailureText'
import {
  type WithdrawSummaryOrder,
  formatWithdrawSummary,
} from '~/pages/IntradayBidding/models/withdrawSummary'

/** 取消の通知に使う札の情報 (一覧項目 / WITHDRAW 応答の注文のどちらでもよい)。 */
export type WithdrawNoticeOrder = {
  id: string
  withdrawState: BiddingIntradayOrderWithdrawState
  // 失敗確定の理由 (AC-BIN-008b)。 トーストの本文を理由で出し分ける。
  withdrawFailureReason: BiddingIntradayOrderWithdrawFailureReason | undefined
  // 取消成立後の札の状態。 成功トーストの本文を 「取消」 / 「部分約定」 で出し分ける。
  bidStatus?: BiddingIntradayOrderStatus
  // 取下可否 (一覧項目だけが持つ)。 一括板取消で一部約定の札の成立を読むのに使う (AC-BIN-112)。
  withdrawable?: boolean
  // 受渡日 (一覧項目だけが持つ)。 一括板取消の結果を選択中の受渡日の札で数えるのに使う (AC-BIN-112)。
  targetDate?: ProtoDate
  timeCode: number
  areaCode: AreaCode
  direction: Side
  orderMethod?: BiddingIntradayOrderOrderMethod
  priceYenPerKwh?: Decimal
  volumeMwh?: Decimal
}

type Observations = Map<string, BiddingIntradayOrderWithdrawState>

// 札ごとに最後に観測した取消の進行状態。 WITHDRAW 応答と一覧の polling の両方が
// 同じ Map に書くので、 どちらが先に届いても PENDING → FAILED の遷移を 1 回だけ拾える。
// 一括板取消 (AC-BIN-112) の結果トーストの追跡も同じ Provider に置き、 一括で受け付けた札と
// 一括の応答を待っている札 (heldIds) は札ごとの通知を出さない。 応答を待つ間に失敗が確定した札は
// heldFailures に持ち、 応答で一括として追わなかった札だけ札ごとに知らせる。
const WithdrawObservationsContext = createContext<{
  observations: MutableRefObject<Observations>
  withdrawnHereIds: MutableRefObject<Set<string>> | undefined
  heldIds: MutableRefObject<Set<string>>
  heldFailures: MutableRefObject<Map<string, WithdrawNoticeOrder>>
  bulk: BulkResultNotice<WithdrawNoticeOrder>
} | null>(null)

/**
 * 時間前入札ページ単位で取消の観測状態を 1 つ持ち、 一覧 (orders) の polling 観測と
 * 配下の WITHDRAW 応答 (useNotifyWithdrawAccepted)・一括板取消の受付
 * (useTrackIntradayBulkWithdraw) で共有する。 AC-BIN-008b の失敗確定通知と AC-BIN-112 の
 * 結果トーストは、 この Provider の内側でしか組み立てられない。
 */
export const IntradayWithdrawNoticeProvider = ({
  orders,
  ordersUpdatedAt = 0,
  selectedTargetDate,
  withdrawnHereIds,
  children,
}: {
  orders: readonly WithdrawNoticeOrder[] | undefined
  /**
   * `orders` を取得した時刻 (React Query の `dataUpdatedAt`)。 一括板取消の受付より前に
   * 取得した一覧を結果の観測に数えないために使う。 一覧を観測しない配置 (`orders` が
   * undefined の詳細モーダル単体の test 等) では省略してよい。
   */
  ordersUpdatedAt?: number
  /**
   * 画面で選択中の受渡日。 一括板取消の結果は、 この受渡日の札の観測で数える (絞り込み・タブには
   * 左右されず、 別の受渡日を表示している間の結果はその受渡日に戻った時に数える。 AC-BIN-112)。
   * 省略時は `orders` のすべての札で数える。
   */
  selectedTargetDate?: ProtoDate
  /** この画面の WITHDRAW 応答を受けた札 id の書き込み先 (AC-BIN-057 の通知の除外集合)。 */
  withdrawnHereIds?: MutableRefObject<Set<string>>
  children: React.ReactNode
}): React.ReactElement => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const observations = useRef<Observations>(new Map())
  const heldIds = useRef<Set<string>>(new Set())
  const heldFailures = useRef<Map<string, WithdrawNoticeOrder>>(new Map())
  const spec = useMemo(
    () => intradayBulkWithdrawNoticeSpec<WithdrawNoticeOrder>(t),
    [t],
  )
  const bulk = useBulkResultNotice(spec, {
    invalidate: () => invalidateIntradayOrderQueries(queryClient),
  })
  const notifyFailures = useWithdrawFailureNotification(
    observations,
    bulk,
    heldIds,
    heldFailures,
  )
  // 札ごとの失敗確定の通知を先に当て、 その後で一括の結果を観測する。 逆にすると、 一括の観測が
  // 結果を出した札を追跡から外した後で札ごとの通知が走り、 一括の札にも札ごとのトーストが出る。
  useEffect(() => {
    if (orders === undefined) return
    notifyFailures(orders)
    bulk.observe(
      selectedTargetDate === undefined
        ? orders
        : orders.filter((order) =>
            isSameProtoDate(order.targetDate, selectedTargetDate),
          ),
      ordersUpdatedAt,
    )
  }, [orders, ordersUpdatedAt, selectedTargetDate, notifyFailures, bulk])
  const shared = useMemo(
    () => ({ observations, withdrawnHereIds, heldIds, heldFailures, bulk }),
    [observations, withdrawnHereIds, heldIds, heldFailures, bulk],
  )
  return (
    <WithdrawObservationsContext.Provider value={shared}>
      {children}
    </WithdrawObservationsContext.Provider>
  )
}

function useShared(): NonNullable<
  React.ContextType<typeof WithdrawObservationsContext>
> {
  const shared = useContext(WithdrawObservationsContext)
  if (shared === null) {
    // 観測状態を共有できないと、 WITHDRAW 応答で記録した PENDING が一覧側に届かず
    // 失敗確定の通知が黙って落ちるため、 配置の誤りは描画時に止める。
    throw new Error(
      'useNotifyWithdrawAccepted must be used within IntradayWithdrawNoticeProvider',
    )
  }
  return shared
}

/** 通知用の札 (Decimal) を、 概要の formatter が受ける素の値にする。 */
const summaryOrderOf = (
  order: Omit<WithdrawNoticeOrder, 'id' | 'withdrawState'>,
): WithdrawSummaryOrder => ({
  timeCode: order.timeCode,
  areaCode: order.areaCode,
  direction: order.direction,
  orderMethod: order.orderMethod,
  priceYenPerKwh: order.priceYenPerKwh?.value,
  volumeMw: order.volumeMwh?.value,
})

function useShowWithdrawFailed(): (
  order: Omit<WithdrawNoticeOrder, 'id' | 'withdrawState'>,
) => void {
  const { t } = useTranslation()
  const showErrorToast = useErrorToast()
  return useCallback(
    (order) => {
      showErrorToast({
        title: t('page:biddingIntraday.withdrawFailedToast.title'),
        description: t(
          withdrawFailureTextKeys(order.withdrawFailureReason).toast,
          {
            summary: formatWithdrawSummary(t, summaryOrderOf(order)),
          },
        ),
        duration: 7000,
      })
    },
    [t, showErrorToast],
  )
}

/**
 * WITHDRAW RPC 成功時のトースト出し分け (AC-BIN-008 / AC-BIN-008b)。
 * 応答の withdrawState が PENDING (JEPX 取消が未完了でリトライ中) なら処理中の
 * warning トースト、 FAILED (再取消の inline 試行が即 terminal になった場合) なら
 * 失敗確定の error トーストを出し、 どちらも 「取消しました」 系の成功トーストは出さない。
 * 応答で見た状態は一覧の観測と同じ Map に記録する。
 */
export function useNotifyWithdrawAccepted(): (
  orderId: string,
  order: WithdrawNoticeOrder | undefined,
  successTitle: string,
) => void {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const showWithdrawFailed = useShowWithdrawFailed()
  const { observations, withdrawnHereIds, bulk } = useShared()
  return useCallback(
    (orderId, order, successTitle) => {
      withdrawnHereIds?.current.add(orderId)
      // 一括板取消で追っていた札を 1 件の板取消で取り消し直したら、 結果は札ごとに知らせる。
      bulk.release(orderId)
      const withdrawState = order?.withdrawState
      if (withdrawState !== undefined) {
        observations.current.set(orderId, withdrawState)
      }
      if (
        order !== undefined &&
        withdrawState === BiddingIntradayOrderWithdrawState.FAILED
      ) {
        showWithdrawFailed(order)
        return
      }
      if (withdrawState === BiddingIntradayOrderWithdrawState.PENDING) {
        // warning type は success/error 専用の useSuccessToast/useErrorToast の
        // 対象外のため appToaster を直接参照する。
        appToaster.create({
          type: 'warning',
          closable: true,
          duration: 5000,
          title: t('page:biddingIntraday.withdrawPendingToast.title'),
          description: t('page:biddingIntraday.withdrawPendingToast.body'),
        })
        return
      }
      const bodyKey =
        order?.bidStatus === BiddingIntradayOrderStatus.PARTIAL_MATCHED
          ? 'page:biddingIntraday.withdrawConfirm.successBody.partialMatched'
          : order?.bidStatus === BiddingIntradayOrderStatus.CANCELLED
            ? 'page:biddingIntraday.withdrawConfirm.successBody.cancelled'
            : undefined
      showSuccessToast({
        title: successTitle,
        description:
          order !== undefined && bodyKey !== undefined
            ? t(bodyKey, {
                summary: formatWithdrawSummary(t, summaryOrderOf(order)),
              })
            : undefined,
      })
    },
    [
      t,
      showSuccessToast,
      showWithdrawFailed,
      observations,
      withdrawnHereIds,
      bulk,
    ],
  )
}

/**
 * 一括板取消 (AC-BIN-112) で受け付けた札の結果を追い始める。 結果は件数をまとめたトーストで
 * 知らせ、 札ごとの失敗確定の通知 (AC-BIN-008b) と「取消を送りました」 (AC-BIN-057) は
 * 出さない (この画面から取り消した札として除外集合にも入れる)。
 */
export function useTrackIntradayBulkWithdraw(): (
  acceptedIds: readonly string[],
) => void {
  const { withdrawnHereIds, bulk } = useShared()
  return useCallback(
    (acceptedIds) => {
      for (const id of acceptedIds) withdrawnHereIds?.current.add(id)
      bulk.track(acceptedIds)
    },
    [withdrawnHereIds, bulk],
  )
}

/**
 * 一括板取消 (AC-BIN-112) の要求を送る前に、 送る札の札ごとの通知を応答まで止める
 * (backend は commit の後に inline で取消を送ってから応答し、 mutation は一覧を読み直してから
 * 返るので、 その間の一覧で取消処理中・失敗確定になる札がある)。 応答を待つ札として、 札ごとの
 * 失敗トースト (AC-BIN-008b) をその時は出さずに持ち (観測は記録する)、 この画面から取り消した札
 * (除外集合) に入れて「取消を送りました」 (AC-BIN-057) も出さない。 返す関数に結果を追う札を渡すと、
 * 応答を待つ札から外し、 それ以外の札 (受け付けなかった札・要求が失敗した時の札) は、 持っていた
 * 失敗を札ごとのトーストで知らせ、 除外集合から、 この要求で入れた分だけ外す。 結果を追う札の失敗は
 * 一括のトーストが件数で知らせるので捨てる。 結果を追う札は先に `useTrackIntradayBulkWithdraw` で
 * 追い始める。
 */
export function useHoldIntradayBulkWithdrawNotices(): (
  ids: readonly string[],
) => (keptIds: readonly string[]) => void {
  const { withdrawnHereIds, heldIds, heldFailures } = useShared()
  const showWithdrawFailed = useShowWithdrawFailed()
  return useCallback(
    (ids) => {
      const held = ids.filter(
        (id) => withdrawnHereIds?.current.has(id) !== true,
      )
      for (const id of held) withdrawnHereIds?.current.add(id)
      for (const id of ids) heldIds.current.add(id)
      return (keptIds) => {
        const kept = new Set(keptIds)
        for (const id of held) {
          if (!kept.has(id)) withdrawnHereIds?.current.delete(id)
        }
        for (const id of ids) {
          heldIds.current.delete(id)
          const failedOrder = heldFailures.current.get(id)
          heldFailures.current.delete(id)
          if (failedOrder !== undefined && !kept.has(id)) {
            showWithdrawFailed(failedOrder)
          }
        }
      }
    },
    [withdrawnHereIds, heldIds, heldFailures, showWithdrawFailed],
  )
}

/**
 * 取消の失敗確定 (PENDING → FAILED) を観測した時に 1 回だけ error トーストを
 * 出す (AC-BIN-008b)。 リトライ限界・GC 到達だけでなく、 再試行している間に札が
 * 失効・全約定で終わった場合も backend が FAILED で返すので同じ遷移で拾う。 リトライ中
 * (PENDING) は鳴らさない。 札ごとの前回観測は WITHDRAW 応答と共有するので、 応答で
 * FAILED を通知済みなら重複せず、 応答で PENDING を受けた札が次の再取得で FAILED に
 * なっていれば通知する。 初回観測が既に FAILED の札 (画面を開く前に確定) は遷移を
 * 見ていないのでトーストは出さない: その札には一覧のチップと詳細のアラートが残る。
 * 一括板取消で追っている札は、 観測だけ記録して札ごとのトーストは出さない (結果は一括の
 * トーストが件数で知らせる。 AC-BIN-112)。 一括の応答を待っている札 (heldIds) は、 観測を記録し、
 * 失敗の確定は heldFailures に持って応答の後に回す (`useHoldIntradayBulkWithdrawNotices`)。
 */
function useWithdrawFailureNotification(
  observations: MutableRefObject<Observations>,
  bulk: Pick<BulkResultNotice<WithdrawNoticeOrder>, 'isTracked'>,
  heldIds: MutableRefObject<Set<string>>,
  heldFailures: MutableRefObject<Map<string, WithdrawNoticeOrder>>,
): (orders: readonly WithdrawNoticeOrder[]) => void {
  const showWithdrawFailed = useShowWithdrawFailed()
  return useCallback(
    (orders) => {
      for (const order of orders) {
        if (
          order.withdrawState === BiddingIntradayOrderWithdrawState.FAILED &&
          observations.current.get(order.id) ===
            BiddingIntradayOrderWithdrawState.PENDING &&
          !bulk.isTracked(order.id)
        ) {
          if (heldIds.current.has(order.id)) {
            heldFailures.current.set(order.id, order)
          } else {
            showWithdrawFailed(order)
          }
        }
        observations.current.set(order.id, order.withdrawState)
      }
    },
    [showWithdrawFailed, observations, bulk, heldIds, heldFailures],
  )
}
